import { clearSenderProxies, fetchSenderProxyAdminConfig, patchSenderProxyAdminConfig } from '@/api/admin';
import { Button } from '@/components/ui/button';
import { InputGroup, InputGroupInput } from '@/components/ui/input-group';
import { Label } from '@/components/ui/label';
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import type { SenderProxyAdminConfig, SenderProxyFormat, SenderProxyProtocol } from '@/entities/sender.entity';
import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';

export default function ProxyPoolSettingsForm({ onSaved }: { onSaved: () => void }) {
	const [config, setConfig] = useState<SenderProxyAdminConfig | null>(null);
	const [loadFailed, setLoadFailed] = useState(false);
	const [confirmClear, setConfirmClear] = useState('');
	const [saving, setSaving] = useState(false);
	const [clearing, setClearing] = useState(false);

	const load = useCallback(async () => {
		setLoadFailed(false);
		try {
			const data = await fetchSenderProxyAdminConfig();
			setConfig(data);
		} catch {
			setConfig(null);
			setLoadFailed(true);
		}
	}, []);

	useEffect(() => {
		void load();
	}, [load]);

	async function handleSave() {
		if (!config) {
			return;
		}
		setSaving(true);
		try {
			const saved = await patchSenderProxyAdminConfig({
				config: {
					enabled: config.enabled,
					protocol: config.protocol,
					format: config.format,
					maxAttempts: config.maxAttempts,
					checkDomain: config.checkDomain,
					checkTimeoutSec: config.checkTimeoutSec,
				},
				poolSettings: config.poolSettings,
			});
			setConfig(saved);
			toast.success('Настройки сохранены');
			onSaved();
		} catch {
			// interceptor
		} finally {
			setSaving(false);
		}
	}

	async function handleClearPool() {
		if (confirmClear !== 'УДАЛИТЬ') {
			toast.error('Введите УДАЛИТЬ для подтверждения');
			return;
		}
		setClearing(true);
		try {
			await clearSenderProxies('УДАЛИТЬ');
			setConfirmClear('');
			toast.success('Пул очищен');
			onSaved();
			void load();
		} catch {
			// interceptor
		} finally {
			setClearing(false);
		}
	}

	if (!config) {
		return (
			<p className='text-xs text-muted-foreground'>
				{loadFailed ? 'Не удалось загрузить настройки пула' : 'Загрузка настроек…'}
			</p>
		);
	}

	return (
		<div className='flex flex-col gap-4 rounded-lg border border-border bg-muted/20 p-4'>
			<p className='text-sm font-medium'>Настройки пула</p>

			<div className='flex items-center justify-between gap-3'>
				<Label htmlFor='proxy-pool-enabled'>Прокси обязательны</Label>
				<Switch
					id='proxy-pool-enabled'
					checked={config.enabled}
					onCheckedChange={enabled => setConfig({ ...config, enabled })}
				/>
			</div>

			<div className='grid gap-3 sm:grid-cols-2'>
				<div className='flex flex-col gap-1.5'>
					<Label>Протокол по умолчанию</Label>
					<Select
						value={config.protocol}
						onValueChange={protocol => setConfig({ ...config, protocol: protocol as SenderProxyProtocol })}
					>
						<SelectTrigger className='w-full'>
							<SelectValue />
						</SelectTrigger>
						<SelectContent>
							<SelectItem value='socks5'>SOCKS5</SelectItem>
							<SelectItem value='http'>HTTP</SelectItem>
						</SelectContent>
					</Select>
				</div>
				<div className='flex flex-col gap-1.5'>
					<Label>Формат по умолчанию</Label>
					<Select value={config.format} onValueChange={format => setConfig({ ...config, format: format as SenderProxyFormat })}>
						<SelectTrigger className='w-full'>
							<SelectValue />
						</SelectTrigger>
						<SelectContent>
							<SelectItem value='auto'>Авто</SelectItem>
							<SelectItem value='host_port_user_pass'>host:port:user:pass</SelectItem>
							<SelectItem value='host_port'>host:port</SelectItem>
							<SelectItem value='url'>URL</SelectItem>
						</SelectContent>
					</Select>
				</div>
			</div>

			<div className='grid gap-3 sm:grid-cols-2'>
				{[
					['maxAttempts', 'Попыток прокси', config.maxAttempts],
					['checkTimeoutSec', 'Таймаут проверки (сек)', config.checkTimeoutSec],
					['restDays', 'Дней отлежки', config.poolSettings.restDays],
					['maxLifetimeRuns', 'Макс. runs на прокси', config.poolSettings.maxLifetimeRuns],
					['maxFailures', 'Failures до cooldown', config.poolSettings.maxFailures],
					['cooldownMinutes', 'Cooldown (мин)', config.poolSettings.cooldownMinutes],
					['reassignAuthRetries', 'Auth reassign retries', config.poolSettings.reassignAuthRetries],
				].map(([key, label, value]) => (
					<div key={key} className='flex flex-col gap-1.5'>
						<Label>{label}</Label>
						<InputGroup>
							<InputGroupInput
								type='number'
								value={String(value)}
								onChange={e => {
									const num = Number(e.target.value);
									if (key in config.poolSettings) {
										setConfig({
											...config,
											poolSettings: { ...config.poolSettings, [key]: num },
										});
									} else {
										setConfig({ ...config, [key]: num } as SenderProxyAdminConfig);
									}
								}}
							/>
						</InputGroup>
					</div>
				))}
			</div>

			<div className='flex flex-col gap-1.5'>
				<Label>Домен проверки</Label>
				<InputGroup>
					<InputGroupInput
						value={config.checkDomain}
						onChange={e => setConfig({ ...config, checkDomain: e.target.value })}
					/>
				</InputGroup>
			</div>

			<Button type='button' className='self-start' disabled={saving} onClick={() => void handleSave()}>
				{saving ? 'Сохранение…' : 'Сохранить настройки'}
			</Button>

			<div className='border-t border-border pt-4 flex flex-col gap-2'>
				<p className='text-sm font-medium text-destructive'>Очистить весь пул</p>
				<InputGroup>
					<InputGroupInput
						value={confirmClear}
						onChange={e => setConfirmClear(e.target.value)}
						placeholder='Введите УДАЛИТЬ'
					/>
				</InputGroup>
				<Button type='button' variant='destructive' className='self-start' disabled={clearing} onClick={() => void handleClearPool()}>
					{clearing ? 'Очистка…' : 'Очистить пул'}
				</Button>
			</div>
		</div>
	);
}
